import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Trophy, Sparkles, Star, Gift, RefreshCcw,
  ChevronRight, Play, CheckCircle2, User,
  AlertCircle, ShieldCheck, ListOrdered,
  ArrowRightCircle, Target, TrophyIcon, Hash, Search
} from 'lucide-react';
import websocketService from '../services/websocket';
import { toast } from '../lib/swal';
import AppButton from './ui/AppButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// --- SUB-COMPONENTE: DigitSlot (Efecto Tragaperras Pro) ---
const DigitSlot = ({ digit }) => {
  const [spinning, setSpinning] = useState(false);
  const [braking, setBraking] = useState(false);
  const prevDigit = useRef(null);

  const numberStrip = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9,
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9,
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9
  ];

  useEffect(() => {
    if (prevDigit.current === null && digit !== null) {
      setSpinning(true);
      const spinTimer = setTimeout(() => {
        setSpinning(false);
        setBraking(true);
        setTimeout(() => setBraking(false), 2000);
      }, 5000);
      return () => clearTimeout(spinTimer);
    }
    prevDigit.current = digit;
  }, [digit]);

  return (
    <div className={`
      relative w-16 h-24 md:w-24 md:h-36 rounded-[1.2rem] md:rounded-[1.8rem] overflow-hidden
      bg-[#0a0c10] border-2 shadow-inner transition-all duration-700
      ${digit !== null && !spinning && !braking ? 'border-orange-500 ring-4 ring-orange-500/20' : 'border-white/10'}
    `}>
      <div className={`absolute inset-0 z-0 opacity-20 transition-opacity duration-1000 ${digit !== null && !spinning && !braking ? 'bg-orange-500 blur-3xl' : 'opacity-0'}`} />
      <div
        className={`
          relative z-10 w-full flex flex-col items-center
          ${spinning ? 'animate-digit-spin' : ''}
          ${braking ? 'transition-transform duration-[2000ms] ease-out' : ''}
          ${!spinning && !braking ? 'transition-none' : ''}
        `}
        style={(!spinning || braking) ? { transform: `translateY(-${(digit * 3.33) + 66.6}% )` } : {}}
      >
        <div className="flex flex-col items-center space-y-0 select-none">
          {numberStrip.map((n, i) => (
            <span
              key={i}
              className={`
                text-4xl md:text-7xl font-black italic tracking-tighter h-24 md:h-36 flex items-center justify-center
                ${!spinning && !braking && n === digit && i >= 20 ? 'text-white brightness-150' : 'text-white'}
                ${!spinning && !braking && n === digit && i >= 20 ? 'drop-shadow-[0_0_30px_rgba(255,255,255,0.8)]' : ''}
              `}
            >
              {n}
            </span>
          ))}
        </div>
      </div>
      <div className="absolute inset-0 z-20 bg-gradient-to-t from-black/80 via-transparent to-white/10 pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-black/60 to-transparent z-20 pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/60 to-transparent z-20 pointer-events-none" />
    </div>
  );
};

// --- SUB-COMPONENTE: DigitDisplay estático (para modo lotería) ---
const DigitDisplay = ({ digit }) => (
  <div className={`
    relative w-16 h-24 md:w-24 md:h-36 rounded-[1.2rem] md:rounded-[1.8rem] overflow-hidden
    bg-[#0a0c10] border-2 shadow-inner transition-all duration-700
    ${digit !== null ? 'border-violet-500 ring-4 ring-violet-500/20' : 'border-white/10'}
  `}>
    {digit !== null && <div className="absolute inset-0 z-0 bg-violet-500/20 blur-3xl" />}
    <div className="relative z-10 w-full h-full flex items-center justify-center">
      <span className={`text-4xl md:text-7xl font-black italic tracking-tighter select-none
        ${digit !== null ? 'text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.8)]' : 'text-white/20'}
      `}>
        {digit !== null ? digit : '?'}
      </span>
    </div>
    <div className="absolute inset-0 z-20 bg-gradient-to-t from-black/80 via-transparent to-white/10 pointer-events-none" />
  </div>
);

// --- Texto animado de expectativa ---
const SuspenseMessages = ({ status, mode }) => {
  const [msgIdx, setMsgIdx] = useState(0);
  const revealMsgs = [
    '🎰 Pronto conoceremos al ganador...',
    '🔥 La emoción está al máximo',
    '💫 Los dígitos giran con fuerza',
    '🎯 Un número está siendo seleccionado',
    '⚡ ¡Atención! El sorteo avanza',
  ];
  const lotteryMsgs = [
    '🏆 Ingresa el número de la lotería',
    '🎫 El número ganador determinará al afortunado',
    '✨ Solo participan boletos aprobados',
    '🔍 El sistema validará el número exacto',
    '🛡️ Sorteo transparente y verificado',
  ];
  const msgs = mode === 'manual_ticket_input' ? lotteryMsgs : revealMsgs;

  useEffect(() => {
    const t = setInterval(() => setMsgIdx(i => (i + 1) % msgs.length), 3500);
    return () => clearInterval(t);
  }, [msgs.length]);

  if (status !== 'ready' && status !== 'drawing') return null;
  return (
    <p className="text-sm md:text-base text-slate-400 font-medium animate-pulse transition-all duration-700 italic text-center px-4">
      {msgs[msgIdx]}
    </p>
  );
};

// --- Lanzador de confeti para ganadores (implementación segura sin canvas-confetti) ---
const launchWinnerConfetti = () => {
  if (typeof document === 'undefined') return;
  const colors = ['#f97316', '#a855f7', '#22c55e', '#eab308', '#ffffff', '#3b82f6'];
  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999;overflow:hidden;';
  document.body.appendChild(container);

  const createParticle = () => {
    const el = document.createElement('div');
    const color = colors[Math.floor(Math.random() * colors.length)];
    const size = 6 + Math.random() * 8;
    const x = Math.random() * 100;
    el.style.cssText = `
      position:absolute;
      width:${size}px;height:${size}px;
      background:${color};
      border-radius:${Math.random() > 0.5 ? '50%' : '2px'};
      left:${x}%;
      top:-10px;
      opacity:1;
      transform:rotate(${Math.random() * 360}deg);
    `;
    container.appendChild(el);

    const duration = 2000 + Math.random() * 2000;
    const drift = (Math.random() - 0.5) * 200;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = elapsed / duration;
      if (progress >= 1) { el.remove(); return; }
      el.style.top = `${-2 + progress * 110}%`;
      el.style.left = `${x + drift * progress}%`;
      el.style.opacity = String(Math.max(0, 1 - progress * 0.8));
      el.style.transform = `rotate(${progress * 720}deg)`;
      requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  };

  // Lanzar oleadas de partículas
  let count = 0;
  const interval = setInterval(() => {
    for (let i = 0; i < 8; i++) createParticle();
    count++;
    if (count > 20) {
      clearInterval(interval);
      setTimeout(() => container.remove(), 4000);
    }
  }, 150);
};

// =============================================
// COMPONENTE PRINCIPAL
// =============================================
const LiveLotteryDraw = ({ sorteo, user }) => {
  const [drawState, setDrawState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFinalDraw, setIsFinalDraw] = useState(true);
  const [manualNumber, setManualNumber] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const prevStatusRef = useRef(null);
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  // Detectar modo: usar draw_mode del sorteo prop; también puede venir en drawState
  const drawMode = drawState?.draw_mode || sorteo?.draw_mode || 'manual_ticket_input';

  useEffect(() => {
    fetchDrawState();

    const handleAnimationComplete = (data) => {
      setDrawState(prev => ({ ...prev, status: 'finished' }));
      if (data && typeof data.is_final !== 'undefined') {
        setIsFinalDraw(data.is_final);
      }
    };

    const handleUpdate = (data) => {
      if (data.sorteo_id === sorteo.id) {
        setDrawState(data);
      }
    };

    websocketService.onLiveAnimationComplete(handleAnimationComplete);
    websocketService.onLiveDrawUpdate(handleUpdate);
    websocketService.joinSorteo(sorteo.id);

    return () => {
      websocketService.offLiveAnimationComplete(handleAnimationComplete);
      websocketService.offLiveDrawUpdate(handleUpdate);
      websocketService.leaveSorteo(sorteo.id);
    };
  }, [sorteo.id]);

  // Lanzar confeti cuando se encuentra ganador
  useEffect(() => {
    if (drawState?.status === 'winner_found' && prevStatusRef.current !== 'winner_found') {
      launchWinnerConfetti();
    }
    prevStatusRef.current = drawState?.status;
  }, [drawState?.status]);

  const fetchDrawState = async () => {
    try {
      const res = await axios.get(`${API}/live-draw/state/${sorteo.id}`);
      setDrawState(res.data);
    } catch (error) {
      console.error('Error fetching draw state:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminAction = async (action) => {
    try {
      const res = await axios.post(`${API}/admin/live-draw/${action}/${sorteo.id}`);
      setDrawState(res.data);
      if (action === 'finalize') toast.success('Sorteo finalizado y ganadores publicados');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ejecutar acción');
    }
  };

  const handleValidateNumber = async () => {
    const num = parseInt(manualNumber, 10);
    if (isNaN(num) || num < 0) {
      toast.error('Ingresa un número válido');
      return;
    }
    setIsValidating(true);
    try {
      const res = await axios.post(
        `${API}/admin/live-draw/validate-number/${sorteo.id}`,
        { numero: num });
      setDrawState(res.data);
      setManualNumber('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al validar número');
    } finally {
      setIsValidating(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-20 space-y-6">
      <RefreshCcw className="w-12 h-12 text-orange-500 animate-spin" />
      <p className="text-[12px] font-black uppercase tracking-[0.4em] text-slate-500">Sincronizando sorteo en vivo...</p>
    </div>
  );

  // VISTA: INICIALIZACIÓN
  if (!drawState || drawState.status === 'off') {
    return (
      <Card className="border-none bg-slate-900 shadow-2xl rounded-[2.5rem] overflow-hidden ring-1 ring-white/10">
        <CardContent className="p-8 md:p-12 text-center space-y-6 relative">
          <div className="absolute inset-0 bg-gradient-to-tr from-orange-600/10 via-transparent to-purple-600/10" />
          <div className="relative z-10 flex flex-col items-center space-y-6">
            <div className="w-24 h-24 bg-orange-600 rounded-[2rem] flex items-center justify-center shadow-2xl shadow-orange-600/40 animate-pulse">
              <Play className="w-12 h-12 text-white fill-white" />
            </div>
            <div className="space-y-3">
              <h3 className="text-3xl md:text-5xl font-black uppercase tracking-tighter italic text-white">
                Sorteo Especial en <span className="text-orange-500">VIVO</span>
              </h3>
              <p className="text-lg text-slate-400 font-medium max-w-lg mx-auto leading-relaxed">
                {drawMode === 'manual_ticket_input'
                  ? 'Modo: Ingreso de Número (Lotería). El administrador ingresará el número ganador manualmente.'
                  : 'Modo: Revelación dígito por dígito. El número se revelará progresivamente.'}
              </p>

              {/* Badge indicador del modo */}
              <div className="flex justify-center">
                <Badge className={`px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border-none
                  ${drawMode === 'manual_ticket_input' ? 'bg-violet-600 text-white' : 'bg-emerald-600 text-white'}`}>
                  {drawMode === 'manual_ticket_input' ? <Hash className="w-3 h-3 inline mr-1" /> : <Sparkles className="w-3 h-3 inline mr-1" />}
                  {drawMode === 'manual_ticket_input' ? 'Modo Ingreso de Número' : 'Modo Revelación Manual'}
                </Badge>
              </div>
            </div>

            {isAdmin ? (
              <AppButton
                variant="primary"
                className="px-12 h-16 rounded-2xl shadow-2xl shadow-orange-600/30 text-lg font-black italic uppercase tracking-wider bg-orange-600 hover:bg-orange-500"
                onClick={() => handleAdminAction('init')}
              >
                INICIALIZAR PANEL DE SORTEO
              </AppButton>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center gap-3 bg-white/5 px-6 py-3 rounded-2xl border border-white/10">
                  <RefreshCcw className="w-5 h-5 text-orange-500 animate-spin" />
                  <span className="text-xs font-black text-white/80 uppercase tracking-widest">Esperando que el Admin inicie...</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const {
    status = 'off',
    revealed_digits = [],
    total_digits = 0,
    winner_data = null,
    prizes = [],
    current_prize_index = 0,
    candidate_number = '',
    resolved_winners = [],
    current_step = -1,
    error_message = null,
  } = drawState || {};

  const currentPrize = (prizes && prizes[Math.min(current_prize_index, prizes.length - 1)]) || { nombre: "Premio" };
  const isLotteryMode = drawMode === 'manual_ticket_input';

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-6 duration-1000">

      {/* 🎰 ESCENARIO PRINCIPAL */}
      <Card className="relative border-none shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] bg-slate-950 rounded-[3rem] overflow-hidden ring-1 ring-white/5">

        {/* Cinematic Backdrop */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-slate-950 to-slate-950" />
          <div className={`absolute top-0 left-0 w-full h-full ${isLotteryMode
            ? 'bg-[radial-gradient(circle_at_50%_0%,rgba(168,85,247,0.15),transparent_70%)]'
            : 'bg-[radial-gradient(circle_at_50%_0%,rgba(234,88,12,0.15),transparent_70%)]'}`} />
          <div className={`absolute -top-24 -left-24 w-96 h-96 rounded-full blur-[120px] ${isLotteryMode ? 'bg-violet-500/10' : 'bg-orange-500/10'}`} />
          <div className={`absolute -bottom-24 -right-24 w-96 h-96 rounded-full blur-[120px] ${isLotteryMode ? 'bg-indigo-500/10' : 'bg-purple-500/10'}`} />

          {/* Partículas festivas de fondo */}
          {(status === 'ready' || status === 'drawing') && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 rounded-full opacity-30 animate-ping"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    backgroundColor: ['#f97316', '#a855f7', '#22c55e', '#eab308'][i % 4],
                    animationDelay: `${Math.random() * 3}s`,
                    animationDuration: `${2 + Math.random() * 3}s`,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <CardContent className="relative z-10 p-6 md:p-8 text-center space-y-4">

          {/* Mode Badge */}
          <div className="flex justify-center">
            <Badge className={`px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.3em] border-none
              ${isLotteryMode ? 'bg-violet-900/60 text-violet-300' : 'bg-emerald-900/60 text-emerald-300'}`}>
              {isLotteryMode ? '🎫 Modo Número de Lotería' : '🎰 Modo Revelación Manual'}
            </Badge>
          </div>

          {/* Header Info */}
          <div className="flex flex-col items-center space-y-6">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Badge className="bg-slate-800 text-slate-300 border border-white/10 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.3em]">
                {sorteo.titulo}
              </Badge>
              <Badge className={`text-white border-none px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.3em] shadow-lg
                ${isLotteryMode ? 'bg-violet-600 shadow-violet-600/30' : 'bg-orange-600 shadow-orange-600/30'}`}>
                Premio {Math.min(current_prize_index, prizes.length - 1) + 1} de {prizes.length}
              </Badge>
            </div>

            <div className="space-y-1">
              <p className={`text-[10px] font-black uppercase tracking-[0.5em] italic
                ${isLotteryMode ? 'text-violet-400' : 'text-orange-500'}`}>SORTEANDO AHORA</p>
              <h2 className="text-3xl md:text-5xl font-black text-white italic tracking-tighter uppercase drop-shadow-lg">
                {currentPrize.nombre}
              </h2>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex justify-center">
            <Badge className={`
              px-8 py-2.5 rounded-full text-[10px] font-black uppercase tracking-[0.3em] shadow-xl border-none
              ${status === 'drawing' ? 'bg-orange-500 text-white animate-pulse' :
                status === 'checking' ? 'bg-purple-600 text-white' :
                  status === 'winner_found' ? 'bg-green-500 text-white' :
                    status === 'no_winner' ? 'bg-red-600 text-white' :
                      status === 'finished' ? 'bg-amber-500 text-white' : 
                        isLotteryMode ? 'bg-violet-800 text-violet-200' : 'bg-slate-800 text-slate-400'}
            `}>
              {status === 'ready' && (isLotteryMode ? '🎫 Listo para Validar Número' : '🔴 Listo para Revelar')}
              {status === 'drawing' && '🔥 Girando Ruleta...'}
              {status === 'checking' && '🛰️ Validando Número...'}
              {status === 'winner_found' && '🏆 ¡PREMIO ADJUDICADO!'}
              {status === 'no_winner' && '❌ NÚMERO SIN DUEÑO VÁLIDO'}
              {status === 'finished' && '🏁 SORTEO COMPLETADO'}
            </Badge>
          </div>

          {/* Mensaje de expectativa animado */}
          <SuspenseMessages status={status} mode={drawMode} />

          {/* 🎰 DÍGITOS: Diferentes según el modo */}
          <div className="flex flex-wrap items-center justify-center gap-3 py-4">
            {isLotteryMode ? (
              // Modo lotería: mostrar dígitos estáticos del número ingresado
              revealed_digits.length > 0
                ? revealed_digits.map((digit, idx) => (
                  <DigitDisplay key={idx} digit={digit} />
                ))
                : (
                  // Placeholder cuando no hay número aún
                  <div className="flex gap-3 opacity-30">
                    {[...Array(Math.max(total_digits, 4))].map((_, idx) => (
                      <DigitDisplay key={idx} digit={null} />
                    ))}
                  </div>
                )
            ) : (
              // Modo revelación: tragaperras animada
              revealed_digits.map((digit, idx) => {
                const isCurrentSpinning = status === 'drawing' && (total_digits - 1 - current_step === idx);
                return (
                  <DigitSlot
                    key={idx}
                    digit={digit}
                    isSpinning={isCurrentSpinning}
                    pos={idx}
                  />
                );
              })
            )}
          </div>

          {/* Indicador de actividad */}
          <div className="flex flex-col items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${status === 'drawing' || (isLotteryMode && status === 'ready') ? 'animate-ping' : ''} 
              ${isLotteryMode ? 'bg-violet-500' : 'bg-orange-600'} ${status !== 'drawing' && !(isLotteryMode && status === 'ready') ? 'bg-slate-700 animate-none' : ''}`} />
          </div>

          {/* 🏆 PANEL DE RESULTADO */}
          <div className="min-h-[100px] flex flex-col items-center justify-center">
            {status === 'winner_found' && winner_data && (
              <div className="w-full max-w-2xl animate-in zoom-in slide-in-from-bottom-12 duration-700 glass-morphism rounded-[2.5rem] p-8 md:p-12 border-green-500/40 relative overflow-hidden">
                {/* Estrellas decorativas */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  {[...Array(8)].map((_, i) => (
                    <Star key={i} className="absolute w-4 h-4 text-yellow-400 opacity-30 animate-pulse"
                      style={{ left: `${10 + i * 12}%`, top: `${10 + (i % 3) * 30}%`, animationDelay: `${i * 0.3}s` }} />
                  ))}
                </div>
                <div className="absolute top-0 right-0 p-8 opacity-20"><TrophyIcon className="w-24 h-24 text-green-500" /></div>
                <div className="relative z-10 flex flex-col items-center space-y-4">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 font-black px-5 py-2 rounded-xl text-[9px] tracking-[0.3em] uppercase">
                    🏆 ¡Felicidades al Ganador!
                  </Badge>
                  <div className="space-y-2">
                    <h2 className="text-4xl md:text-6xl font-black text-white italic tracking-tighter uppercase">{winner_data.nombre}</h2>
                    <div className="flex items-center justify-center gap-4 flex-wrap">
                      <span className={`font-black text-xl md:text-2xl italic tracking-widest
                        ${isLotteryMode ? 'text-violet-400' : 'text-orange-500'}`}>#{candidate_number}</span>
                      <div className="w-1 h-6 bg-white/10" />
                      <span className="text-slate-400 font-bold text-xs uppercase">{winner_data.email}</span>
                    </div>
                    <p className="text-green-400 font-black text-sm uppercase tracking-widest">
                      Premio: {winner_data.prize_name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {status === 'no_winner' && (
              <div className="w-full max-w-xl animate-in zoom-in duration-500 p-10 rounded-[3rem] glass-morphism border-red-500/30 text-center space-y-5">
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto border-2 border-red-500/20">
                  <AlertCircle className="w-10 h-10 text-red-500" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-3xl font-black italic uppercase tracking-tighter text-white">
                    {candidate_number ? `Boleto #${candidate_number}` : 'Sin Ganador'}
                  </h4>
                  <p className="text-lg text-red-400 font-black uppercase tracking-widest">Número sin dueño válido</p>
                  <p className="text-sm text-slate-500 font-medium">
                    {error_message || 'El boleto no fue vendido o no está aprobado para participar.'}
                  </p>
                  {isLotteryMode && (
                    <p className="text-xs text-slate-600 italic">Intenta con el siguiente número de referencia de la lotería.</p>
                  )}
                </div>
              </div>
            )}

            {status === 'finished' && (
              <div className="absolute inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-500 rounded-[2.5rem]">
                <div className="text-center space-y-6 max-w-md">
                  <div className="w-24 h-24 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6 ring-2 ring-amber-500/30">
                    <Trophy className="w-12 h-12 text-amber-400 animate-bounce" />
                  </div>
                  <h3 className="text-4xl font-black text-white italic uppercase tracking-tighter">
                    {isFinalDraw ? '¡Sorteo Completado!' : 'Etapa Finalizada'}
                  </h3>
                  <p className="text-white/70 text-lg font-medium">
                    {isFinalDraw
                      ? 'Todos los premios han sido adjudicados. ¡Gracias por participar!'
                      : 'Esta etapa ha concluido. El sorteo volverá a estar disponible pronto.'}
                  </p>
                  <div className="pt-6">
                    {isAdmin ? (
                      <AppButton
                        variant="primary"
                        size="xl"
                        className="w-full rounded-2xl bg-orange-600 hover:bg-orange-500 text-white font-black italic uppercase tracking-widest h-16 shadow-2xl shadow-orange-600/40 border-none"
                        onClick={() => { handleAdminAction('finalize'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      >
                        FINALIZAR Y PUBLICAR
                      </AppButton>
                    ) : (
                      <AppButton
                        variant="white"
                        size="xl"
                        className="w-full rounded-2xl bg-white text-slate-950 font-black italic uppercase tracking-widest h-16"
                        onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); navigate('/'); }}
                      >
                        VOLVER AL INICIO
                      </AppButton>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 🔧 PANEL DE CONTROL ADMIN */}
          {isAdmin && status !== 'finished' && (
            <div className="pt-4 flex flex-wrap items-center justify-center gap-4">

              {/* === MODO INGRESO DE NÚMERO (LOTERÍA) === */}
              {isLotteryMode && (
                <>
                  {(status === 'ready' || status === 'no_winner') && (
                    <div className="w-full max-w-md space-y-3">
                      <p className="text-slate-400 text-sm font-bold uppercase tracking-widest text-center">
                        Ingresa el número de la lotería:
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="number"
                          min="0"
                          value={manualNumber}
                          onChange={e => setManualNumber(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleValidateNumber()}
                          placeholder="Ej: 35209"
                          className="flex-1 h-16 bg-slate-800 border border-violet-500/30 rounded-2xl px-6 text-white text-2xl font-black tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-violet-500 placeholder:text-slate-600"
                        />
                        <AppButton
                          variant="primary"
                          className="h-16 px-8 rounded-2xl bg-violet-600 hover:bg-violet-500 shadow-xl shadow-violet-600/30 text-base font-black uppercase italic border-none whitespace-nowrap"
                          onClick={handleValidateNumber}
                          disabled={isValidating || !manualNumber}
                          icon={isValidating ? RefreshCcw : Search}
                        >
                          {isValidating ? 'VALIDANDO...' : 'BUSCAR'}
                        </AppButton>
                      </div>
                      <p className="text-[11px] text-slate-600 text-center italic">
                        Solo participan boletos aprobados y pagados.
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* === MODO REVELACIÓN MANUAL === */}
              {!isLotteryMode && (
                <>
                  {status === 'ready' && (
                    <AppButton
                      variant="primary"
                      className="px-10 h-16 rounded-2xl bg-orange-600 hover:bg-orange-500 shadow-xl shadow-orange-600/40 text-lg font-black uppercase italic border-none"
                      onClick={() => handleAdminAction('start-reveal')}
                      icon={Play}
                    >
                      INICIAR REVELACIÓN
                    </AppButton>
                  )}
                  {status === 'drawing' && (
                    <AppButton
                      variant="primary"
                      className="px-10 h-16 rounded-2xl bg-purple-600 hover:bg-purple-500 shadow-xl shadow-purple-600/40 text-lg font-black uppercase italic border-none"
                      onClick={() => handleAdminAction('next-digit')}
                      icon={ChevronRight}
                    >
                      SIGUIENTE DÍGITO
                    </AppButton>
                  )}
                  {status === 'checking' && (
                    <AppButton
                      variant="primary"
                      className="px-10 h-16 rounded-2xl bg-white text-slate-950 hover:bg-slate-100 shadow-xl shadow-white/20 text-lg font-black uppercase italic border-none"
                      onClick={() => handleAdminAction('check-winner')}
                      icon={ShieldCheck}
                    >
                      REVELAR GANADOR
                    </AppButton>
                  )}
                </>
              )}

              {/* CONTROLES COMUNES A AMBOS MODOS */}
              {status === 'no_winner' && (
                <AppButton
                  variant="primary"
                  className="px-14 h-20 rounded-3xl bg-red-600 hover:bg-red-500 shadow-2xl shadow-red-600/30 text-xl font-black uppercase italic border-none"
                  onClick={() => handleAdminAction('reset-prize')}
                  icon={RefreshCcw}
                >
                  INTENTAR CON OTRO NÚMERO
                </AppButton>
              )}

              {status === 'winner_found' && (
                <AppButton
                  variant="primary"
                  className="px-14 h-20 rounded-3xl bg-green-600 hover:bg-green-500 shadow-2xl shadow-green-600/30 text-xl font-black uppercase italic border-none"
                  onClick={() => handleAdminAction('next-prize')}
                  icon={ArrowRightCircle}
                >
                  SIGUIENTE PREMIO
                </AppButton>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 📊 RESUMEN DE PREMIOS Y TRANSPARENCIA */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Card className="glass-morphism border-none rounded-[3rem] overflow-hidden">
          <div className="p-8 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ListOrdered className="w-6 h-6 text-orange-500" />
              <h4 className="text-xl font-black text-white italic uppercase tracking-tighter">Lista de Premios</h4>
            </div>
            <Badge className="bg-white/5 text-slate-400 border-none font-bold">{prizes.length} Premios</Badge>
          </div>
          <CardContent className="p-8 space-y-4">
            {prizes.map((p, idx) => {
              const winner = resolved_winners.find(w => w.prize_index === idx);
              return (
                <div
                  key={idx}
                  className={`
                    flex items-center justify-between p-5 rounded-3xl border transition-all
                    ${idx === current_prize_index ? (isLotteryMode ? 'bg-violet-600/10 border-violet-500/40 ring-1 ring-violet-500/20' : 'bg-orange-600/10 border-orange-500/40 ring-1 ring-orange-500/20') : 'bg-white/[0.02] border-white/5'}
                  `}
                >
                  <div className="flex items-center gap-4">
                    <div className={`
                      w-12 h-12 rounded-2xl flex items-center justify-center font-black
                      ${idx === current_prize_index ? (isLotteryMode ? 'bg-violet-500 text-white animate-pulse' : 'bg-orange-500 text-white animate-pulse') :
                        winner ? 'bg-green-500/20 text-green-500' : 'bg-white/5 text-slate-600'}
                    `}>
                      {idx + 1}
                    </div>
                    <div>
                      <p className={`font-black uppercase tracking-tight italic ${idx === current_prize_index ? 'text-white' : 'text-slate-400'}`}>
                        {p.nombre}
                      </p>
                      {winner && <p className="text-[10px] font-bold text-green-500 uppercase tracking-widest mt-1">Ganador: {winner.nombre}</p>}
                    </div>
                  </div>
                  {winner ? (
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  ) : idx === current_prize_index ? (
                    <div className={`w-3 h-3 rounded-full animate-ping ${isLotteryMode ? 'bg-violet-500' : 'bg-orange-500'}`} />
                  ) : (
                    <Target className="w-5 h-5 text-slate-800" />
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="glass-morphism border-none rounded-[3rem] overflow-hidden flex flex-col justify-center items-center p-12 text-center group">
          <div className={`w-32 h-32 rounded-[3rem] flex items-center justify-center mb-8 border transition-all duration-700
            ${isLotteryMode
              ? 'bg-violet-600/10 border-violet-500/20 group-hover:bg-violet-600 group-hover:rotate-6'
              : 'bg-orange-600/10 border-orange-500/20 group-hover:bg-orange-600 group-hover:rotate-6'}`}>
            <ShieldCheck className={`w-16 h-16 group-hover:text-white transition-colors ${isLotteryMode ? 'text-violet-500' : 'text-orange-500'}`} />
          </div>
          <h4 className="text-3xl font-black text-white italic uppercase tracking-tighter mb-4">Sorteo Transparente</h4>
          <p className="text-slate-400 font-medium leading-relaxed max-w-xs">
            {isLotteryMode
              ? 'Solo participan boletos con pago aprobado. El número exacto ingresado determina el ganador.'
              : 'Todos los números se generan en tiempo real. Solo participan boletos con pago aprobado y confirmado.'}
          </p>
          <div className="mt-8 flex items-center gap-2 text-[10px] font-black text-slate-600 uppercase tracking-widest">
            <ShieldCheck className="w-4 h-4" />
            Protección WishWay Verificada
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LiveLotteryDraw;
